<template>
  <div class="language-selector" v-if="selectedLang" data-testid="language-picker-container">
    <h3 class="mb-2 self-stretch justify-start text-white text-md font-bold" data-testid="language-picker-selected-title">{{ $t('languagepicker_selected')}}</h3>
    <div class="selected-language lang-item mb-4" @click="expandSelector" data-testid="language-picker-selected-item">
      <span v-if="showFlags" class="lang-flag" data-testid="language-picker-selected-flag"><img :src="'/data/flags/' + selectedLang.code + '.png'"/></span>
      <span class="lang-title" data-testid="language-picker-selected-name">{{ selectedLang.name }}</span>
    </div>

    <h3 class="mb-2 self-stretch justify-start text-white text-md font-bold" data-testid="language-picker-available-title">{{ $t('languagepicker_available')}}</h3>
    <div class="expanded-selector" :class="{visible: selectorExpanded}" data-testid="language-picker-expanded-selector">
      <header class="selector-header" v-if="expandedLanguage" data-testid="language-picker-header">
        <a href="javascript:;" class="back-arrow-btn" @click="selectorBack" data-testid="language-picker-back-button">{{ $t('common_back') }}</a>
      </header>

      <div class="h-96 scrollbar-thumb-rounded-full scrollbar-thin scrollbar-track-rounded-full scrollbar scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent overflow-y-scroll" data-testid="language-picker-scroll-area">
        <ul class="root-list pr-3" v-show="!expandedLanguage" data-testid="language-picker-root-list">
          <li class="root-list__item lang-item lang-item--inlist"
              v-for="(lang, index ) in sortLanguages" :key="'lang_' + index"
              @click="clickLanguage(lang)"
              :data-testid="`language-picker-item-${lang.code}`"
              data-testid-type="language-item"
          >
            <span v-if="showFlags" class="lang-flag" :data-testid="`language-picker-flag-${lang.code}`"><img :src="'/data/flags/' + getLangFlag(lang) + '.png'"/></span>
            <span class="lang-title" :data-testid="`language-picker-name-${lang.code}`">{{ lang.name }}</span>
            <span class="lang-arrow" v-if="hasDialects(lang)" :data-testid="`language-picker-arrow-${lang.code}`"><svg width="12" height="19" viewBox="0 0 12 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.2325 0L0 2.2325L7.25167 9.5L0 16.7675L2.2325 19L11.7325 9.5L2.2325 0Z" fill="#505050"/>
                </svg>
              </span>
          </li>
        </ul>

        <div class="expanded-language"
             v-for="(lang, index) in languages"
             :key="'dialects_' + index"
             :class="{ visible: expandedLanguage && expandedLanguage == lang.code }"
             v-show="expandedLanguage && expandedLanguage == lang.code"
             :data-testid="`language-picker-expanded-${lang.code}`"
        >
          <ul class="child-list" v-if="hasDialects(lang)" :class="{visible: expandedLanguage && expandedLanguage == lang.code}"
              :data-testid="`language-picker-dialect-list-${lang.code}`"
          >
            <li class="root-list__item lang-item lang-item--inlist"
                v-for="(dialect, index ) in sortDialects(lang)" :key="'dialect_' + index"
                @click.stop="clickLanguage(dialect)"
                :data-testid="`language-picker-dialect-${dialect.code}`"
                data-testid-type="dialect-item"
            >
              <span v-if="showFlags" class="lang-flag" :data-testid="`language-picker-dialect-flag-${dialect.code}`"><img :src="'/data/flags/' + dialect.code + '.png'"/></span>
              <span class="lang-title" :data-testid="`language-picker-dialect-name-${dialect.code}`">{{ dialect.name }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Constants } from '@/cfg/constants';
// @ is an alias to /src
//import { mapState } from 'vuex';

export default {
  name: 'LanguageSelector',
  props: {
    initial: {
      type: String,
      required: true,
      default: Constants.DEFAULT_LANGUAGE
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
      required: false
    },
    showFlags: {
      type: Boolean,
      default: true,
      required: false
    }
  },
  data() {
    return {
      selected: false,
      expandedLanguage: false,
    }
  },
  methods: {
    expandSelector() {
      this.selectorExpanded = true;
      this.$emit('expanded');
    },
    clickLanguage(lang) {
      if (this.hasDialects(lang)) {
        this.setExpandedLanguage(lang.code);
      } else {
        if (lang.dialects) {
          this.selected = lang.dialects[0].code;
        } else {
          this.selected = lang.code;
        }
        if (this.closeOnSelect) {
          this.$emitter.emit('modalClose')
        }
        this.$emit('select', this.selected);
        this.selectorExpanded = false;
        this.setExpandedLanguage(false);
      }
    },
    getLangFlag(lang) {
      return lang.code;
    },
    selectorBack() {
      if (this.expandedLanguage) {
        this.expandedLanguage = false;

      } else {
        this.selectorExpanded = false;
        this.$emit('back');
      }
    },
    hasDialects(lang) {
      return lang.dialects && lang.dialects.length > 1;
    },
    setExpandedLanguage(expanded) {
      this.expandedLanguage = expanded;
    },
    sortDialects(lang) {
      const activeLocale = this.$i18n.locale;
      const translations = this.$i18n.messages?.[activeLocale];
      return [...lang.dialects].map(dialect => {
        const shortCode = `dialect_${dialect.code}`;
        const matchingKey = translations ? Object.keys(translations).find(key =>
            key.startsWith(shortCode)) : null;
        return {
          ...dialect,
          name: matchingKey ? this.$t(matchingKey) : dialect.native_name || dialect.name
        };
      }).sort((a,b) => a.name.localeCompare(b.name));
    }
  },
  mounted() {
  },
  computed: {
    ...mapState('lang', ['languages']),

    sortLanguages() {
      const activeLocale = this.$i18n.locale;
      const translations = this.$i18n.messages?.[activeLocale];
      return [...this.languages].map(lang => {
        const shortCode = `language_${lang.code}`;
        const matchingKey = translations ? Object.keys(translations).find(key =>
            key.startsWith(shortCode)) : null;
        return {
          ...lang,
          name: matchingKey ? this.$t(matchingKey) : lang.native_name || lang.name
        };
      }).sort((a,b) => a.name.localeCompare(b.name));
    },

    selectedLang() {
      let selectedLang = false;

      this.languages.forEach(lang => {
        if (lang.code == this.selectedLangCode && lang.dialects.length <= 1) {
          selectedLang = lang;
          return;
        } else {
          lang.dialects.forEach(dialect => {
            if (dialect.code == this.selectedLangCode) {
              selectedLang = dialect;
              return;
            }
          });
        }
      });
      return selectedLang;
    },
    selectedLangCode() {
      return this.selected || this.initial || Constants.DEFAULT_LANGUAGE
    }
  }
}
</script>

<style lang="scss" scoped>
.lang-item {
  display: flex;
  align-items: center;
  cursor: pointer;

  &--inlist {
    border-bottom: 1px solid #505050;
    height: 44px;

  }
}

.lang-item.selected-language {
  border: 2px solid #505050;
  border-radius: 10px;
  height: 44px;
  padding-left: 10px;
}

.lang-flag {
  height: 20px;
  display: flex;
  max-width: 30px;

  img {
    max-width: 100%;
  }
}

.lang-title {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.18rem;
  color: #fff;
  margin-left: 10px;
}

.lang-arrow {
  display: flex;
  margin-left: auto;
  margin-right: 10px;
}

.expanded-selector {
  z-index: 2;

  @media all and (max-width: 1100px) {
    width: 100%;
  }

  &.visible {
    opacity: 1;
    visibility: visible;
  }

  ul {
    margin: 0;
    padding: 0;

    li:last-child {
      border-bottom: none;
    }
  }
}

.ps {
  height: 100%;
  opacity: 0;
  visibility: hidden;
  border: 1px solid #505050;
  border-radius: 10px;
  padding: 0 0 0 10px;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
}

.child-list {
  z-index: 2;
  /*background: #1D1C1C;*/
  width:100%;
  height: 100%;
  left: 0;
  padding-right: 10px !important;
}

.profile__container {
  padding-top: 70px;
}

.profile-form {
}

.expanded-language {
  width: 100%;
  visibility: hidden;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
}


.selector-header {
  padding: 4px 0;
}

@media all and (max-width: 1100px) {
  .selector-header {

    position: sticky;
    top: 0;
    z-index: 1000;
    background: #1D1C1C;
    margin: 0;
  }
}


</style>
